import { Registry } from '@lightningjs/sdk'

import { carouselsPrefix } from '../constants/general'
import { AppConfig } from '../services/appConfig'
import { TvBrowseLayoutModel } from '../types/flo-experience'
import { isUserSignedIn } from '../utils/authentication'
import { genericError, networkError } from '../widgets/modal'
import CarouselsHandler from './handlers/carouselsHandler'

const HOME_REFRESH_INTERVAL = 60000 // 1 min

export default class Home extends CarouselsHandler {
  private _siteId: string
  private _limit: string
  private _offset: string
  private _data: TvBrowseLayoutModel
  private _homeRefreshInterval
  private _pageName = ''

  _onUrlParams({ siteId }: { siteId: string }) {
    const urlParams = new URLSearchParams(window.location.href.split('?')[1])
    this._siteId = siteId
    this._limit = urlParams.get('limit') ?? '3'
    this._offset = urlParams.get('offset') ?? '0'
  }

  async _active() {
    this._isRoot = !this._siteId

    this._setState('NotReady')

    await this.getInitialHomeData()

    this._data.container.items =
      this._lastCarousels.length > 0 ? this._lastCarousels : this._data.container.items

    this.tvBrowseLayout.data = this._data

    this.restoreFocus()

    this._setState('Ready')

    // The refresh should happen after the page
    // is mounted and every 60s after that
    this.refreshHome()
    this.setHomeRefreshInterval()
  }

  _inactive() {
    // This ensures next time we visit the page
    // we don't use the prefetched but instead
    // we fetch new and fresh carousels, in case of
    // any changes
    AppConfig.shared.appSettings.homeData = null
    this.unsetHomeRefreshInterval()
  }

  get pageText() {
    return this._pageName
  }

  async getInitialHomeData() {
    if (this._siteId) {
      const data = await AppConfig.shared.experienceApi.getHome({
        site_id: this._siteId,
        limit: this._limit,
        offset: this._offset,
      })

      if (data?.error) {
        const error = data.isNetworkError ? networkError : genericError
        this.setModal(true, error)
        return
      }

      this._pageName = `${data?.analytics?.extra_context?.subpage_category ?? ''} ${
        data?.analytics?.extra_context?.page_category ?? ''
      }`
      this._data = data
    } else {
      // This should give the user the impression that the page is
      // loading even if the information is saved, plus we can catch any errors
      // and not force the async/await to be resolved without assigning any data
      const data = await AppConfig.shared.experienceApi.getHome({
        limit: AppConfig.shared.experienceApi.homeInitialPageSize * 2,
        offset: 0,
      })

      if (!AppConfig.shared.appSettings.homeData && data?.error) {
        const error = data.isNetworkError ? networkError : genericError
        this.setModal(true, error)
        return
      }

      this._pageName = 'Home'
      this._data = AppConfig.shared.appSettings.homeData ?? data
    }
  }

  setHomeRefreshInterval() {
    this._homeRefreshInterval = Registry.setInterval(() => {
      this.refreshHome()
    }, HOME_REFRESH_INTERVAL)
  }

  unsetHomeRefreshInterval() {
    if (this._homeRefreshInterval) {
      Registry.clearInterval(this._homeRefreshInterval)
    }
  }

  async refreshHome() {
    const matches = []
    const userIsSignedIn = isUserSignedIn()
    const data = await AppConfig.shared.experienceApi.getHome({
      ...(this._siteId && { site_id: this._siteId }),
      limit: AppConfig.shared.experienceApi.homeInitialPageSize,
      offset: 0,
    })

    if (!data.error) {
      const items = (data as TvBrowseLayoutModel)?.container?.items || []

      if (!items) return

      items.forEach((item) => {
        if (item?.id?.includes(carouselsPrefix.LIVE_AND_UPCOMING)) {
          matches.push(item)
        }

        if (
          userIsSignedIn &&
          (item?.id?.includes(carouselsPrefix.FAVORITES) ||
            item?.id?.includes(carouselsPrefix.CONTINUE_WATCHING))
        ) {
          matches.push(item)
        }
      })
    }

    if (matches.length > 0) {
      this.tvBrowseLayout.tvRowsContainer.refreshCarousels(matches)
      this.checkNavigationPointersVisibility()
    }
  }
}
